import {Component, Injector} from '@angular/core';
import {Observable} from "rxjs";
import {NotificationsService} from "../../../../services/notifications.service";
import {NotificationViewModel} from "../Model/NotificationViewModel";
import {CreateNotificationComponent} from "../create-notification.component";
import {EditNotificationComponent} from "../edit-notification.component";
import { IManagableObject } from "../../IManagableObject";
import { GroupBaseAdminPanelComponent } from '../../base-admin-panel/group-base-admin-panel.component';
import { ContainersService } from 'app/services/containers.service';
import { ContainerViewModel } from '../../ContainerViewModel';
import { ObjectGroupViewModel } from '../../ObjectGroupViewModel';
import { GroupManagementDialogComponent } from '../../group-management-dialog/group-management-dialog.component';
import { ObjectsManagementDialogComponent } from '../../objects-management-dialog/objects-management-dialog.component';
import { matchesSearchRule } from '../../../shared/pipes/filterByProperty';

@Component({
    selector: '[app-notifications]',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.css'],
    standalone: false
})
export class NotificationsComponent extends GroupBaseAdminPanelComponent {
  public term: string;
  private service: NotificationsService;
  private containersService: ContainersService;
  public containerFilterId: number = 0;
  public containers: ContainerViewModel[] = [];
  private containerNameById = new Map<number, string>();

  constructor(protected injector: Injector) {
    super(injector);
    this.service = injector.get(NotificationsService);
    this.containersService = injector.get(ContainersService);
  }

  protected Refresh() {
    this.loadContainers();

    this.service.GetNotifications()
      .subscribe((notifications) => {
        this.objects = notifications;
      });
    this.service.GetGroups().subscribe(groups => {
      this.groupFilter.groups = groups;
    });
  }

  public get filteredNotifications(): NotificationViewModel[] {
    let items = this.objects as NotificationViewModel[];

    const term = this.filter.term ?? "";
    if (term) {
      items = items.filter(notification => matchesSearchRule(notification.Name, term));
    }

    if (this.filter.group?.Id > 0) {
      const groupIds = new Set(this.filter.group.objects ?? []);
      items = items.filter(notification => groupIds.has(notification.Id));
    }

    if (this.containerFilterId > 0) {
      items = items.filter(notification => notification.ContainerId === this.containerFilterId);
    }

    return items;
  }

  public getContainerName(containerId?: number): string {
    if (!containerId)
      return "";

    return this.containerNameById.get(containerId) ?? "";
  }

  protected doDeleteObject(id: number): Observable<any> {
    return this.service.delete(id);
  }

  public openEditModal(command: NotificationViewModel) {
    this.prepareModal(EditNotificationComponent);
    this.componentRef.instance.open(command);
  }

  public openCreateModal() {
    this.prepareModal(CreateNotificationComponent);
    this.componentRef.instance.open(new NotificationViewModel());
  }
  public createDublicate(object: NotificationViewModel)
  {
    var clone = Object.assign({}, object);
    clone.Name = "";
    clone.Id = 0;
    this.prepareModal(CreateNotificationComponent);
    this.componentRef.instance.open(clone);
  }

  protected doCreateGroup(input: IManagableObject): Observable<any> {
    return this.service.CreateGroup(input);
  }

  protected doDeleteGroup(input: IManagableObject): Observable<any> {
    return this.service.DeleteGroup(input);
  }

  public override addGroup() {
    if (!this.containers.length) return;

    this.groupMgmtDialog = this.dialogService.open(this.groupsContainer, GroupManagementDialogComponent);
    this.groupMgmtDialog.instance.containers = this.containers;
    this.groupMgmtDialog.instance.readonlyContainer = false;
    this.initNotificationGroupDialog();

    const group = new ObjectGroupViewModel();
    group.ContainerId = this.getInitialGroupContainerId();
    this.groupMgmtDialog.instance.open(group);
  }

  public override editGroup(group: ObjectGroupViewModel) {
    this.groupMgmtDialog = this.dialogService.open(this.groupsContainer, GroupManagementDialogComponent);
    this.groupMgmtDialog.instance.containers = this.containers ?? [];
    this.groupMgmtDialog.instance.readonlyContainer = true;
    this.initNotificationGroupDialog();
    this.groupMgmtDialog.instance.open(group);
  }

  private loadContainers() {
    this.containersService.GetContainers().subscribe(containers => {
      this.containers = [...(containers ?? [])]
        .sort((a, b) => (a.Name ?? "").localeCompare(b.Name ?? ""));
      this.containerNameById = new Map(this.containers.map(container => [container.Id, container.Name] as [number, string]));
      if (this.containerFilterId > 0 && !this.containers.some(container => container.Id === this.containerFilterId))
        this.containerFilterId = 0;
    });
  }

  private initNotificationGroupDialog() {
    const allNotifications = this.objects.map(x => Object.assign({}, x)) as NotificationViewModel[];

    this.groupMgmtDialog.instance.onSubmit.subscribe(() => {
      const input = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
      input.objects = this.groupMgmtDialog.instance.objects.map(notification => notification.Id);
      this.doCreateGroup(input).subscribe(() => {
        this.Refresh();
      });
      this.groupMgmtDialog.instance.close();
    });

    this.groupMgmtDialog.instance.onOpen.subscribe(() => {
      const group = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
      this.groupMgmtDialog.instance.objects = this.getVisibleGroupNotifications(allNotifications, group.ContainerId, group.objects)
        .filter(notification => (group.objects ?? []).indexOf(notification.Id) >= 0)
        .map(notification => {
          notification.IsChecked = false;
          return notification;
        });
    });

    this.groupMgmtDialog.instance.onAddObjects.subscribe(() => {
      this.objectsListDialog = this.dialogService.open(this.objectsListContainer, ObjectsManagementDialogComponent);
      this.objectsListDialog.instance.onOpen.subscribe(() => {
        const group = this.groupMgmtDialog.instance.object as ObjectGroupViewModel;
        this.objectsListDialog.instance.groups = this.groupFilter.groups;
        this.objectsListDialog.instance.objects = this.getVisibleGroupNotifications(allNotifications, group.ContainerId, group.objects)
          .filter(notification => this.groupMgmtDialog.instance.objects.find(existing => existing.Id == notification.Id) == undefined)
          .map(x => Object.assign({}, x))
          .map(notification => {
            notification.IsChecked = false;
            return notification;
          });
      });

      this.objectsListDialog.instance.onSubmit.subscribe(() => {
        this.objectsListDialog.instance.objects.forEach(notification => {
          if (notification && notification.IsChecked) {
            if (this.groupMgmtDialog.instance.objects.indexOf(notification) < 0) {
              notification.IsChecked = false;
              this.groupMgmtDialog.instance.objects.push(notification);
            }
          }
        });
        this.objectsListDialog.instance.close();
      });

      this.objectsListDialog.instance.open();
    });
  }

  private getVisibleGroupNotifications(
    allNotifications: NotificationViewModel[],
    containerId?: number,
    selectedIds?: number[]
  ): NotificationViewModel[] {
    const ids = selectedIds ?? [];
    return allNotifications.filter(notification => {
      if (ids.includes(notification.Id))
        return true;

      if (!containerId)
        return false;

      return notification.ContainerId === containerId;
    });
  }

  private getInitialGroupContainerId(): number | undefined {
    if (this.containerFilterId > 0 && this.containers.some(container => container.Id === this.containerFilterId))
      return this.containerFilterId;

    return this.containers[0]?.Id;
  }
}
